import type { DashboardPerformanceSeries } from '../../../dashboard/components/DashboardPerformanceChart';
import { DashboardChartUnit } from '../../../dashboard/dashboardChartAxes';
import { DashboardDatePreset, type DashboardDateRange } from '@/components/dashboard/model';
import type { ReportRequest, ReportResponse } from '../../../reporting/reportExplorer/model';
import {
  ReportDateRangeType,
  ReportDimension,
  ReportMetric,
  ReportType,
} from '../../../reporting/reportExplorer/reportEnums';
import { reportMetricDefinitions } from '../../../reporting/reportExplorer/catalog';
import {
  formatReportMetricValue,
  numericReportValue,
  reportMetricLabel,
} from '../../../reporting/reportExplorer/format';
import { formatCurrency } from '../../../../lib/format';
import { dashboardT } from '../../../../lib/messages';
import { IntegrationDashboardMetric } from './widgetModel';

export const integrationReportMetric: Record<IntegrationDashboardMetric, ReportMetric | undefined> =
  {
    [IntegrationDashboardMetric.BidRequests]: ReportMetric.DpAdRequests,
    [IntegrationDashboardMetric.BidResponses]: ReportMetric.Bids,
    [IntegrationDashboardMetric.WonBids]: ReportMetric.WonBids,
    [IntegrationDashboardMetric.FilledImpressions]: ReportMetric.FilledImpressions,
    [IntegrationDashboardMetric.AdImpressions]: ReportMetric.AdImpressions,
    [IntegrationDashboardMetric.Revenue]: ReportMetric.Revenue,
    [IntegrationDashboardMetric.Profit]: ReportMetric.NetworkProfit,
    [IntegrationDashboardMetric.Margin]: ReportMetric.NetworkMarginPct,
    [IntegrationDashboardMetric.Ecpm]: undefined,
    [IntegrationDashboardMetric.Srpm]: ReportMetric.Srpm,
  };

const allIntegrationReportMetrics = Object.values(integrationReportMetric).filter(
  (metric): metric is ReportMetric => metric !== undefined,
);

export function integrationDashboardMetricLabel(metric: IntegrationDashboardMetric) {
  const reportMetric = integrationReportMetric[metric];
  return reportMetric === undefined
    ? dashboardT('integrations.ecpm')
    : reportMetricLabel(reportMetric);
}

export function formatIntegrationMetric(
  value: number | undefined,
  metric: IntegrationDashboardMetric,
) {
  const reportMetric = integrationReportMetric[metric];
  return reportMetric === undefined
    ? formatCurrency(value)
    : formatReportMetricValue(value, reportMetric);
}

export function integrationEcpm(row: ReportResponse['total']) {
  const revenue = numericReportValue(row?.revenue ?? undefined);
  const impressions = numericReportValue(row?.adImpressions ?? undefined);
  if (revenue === undefined || impressions === undefined || impressions <= 0) return undefined;
  return (revenue / impressions) * 1000;
}

export function buildIntegrationDashboardReportRequest(
  integrationId: string,
  range: DashboardDateRange,
): ReportRequest {
  const timeDimension =
    range.dateFrom === range.dateTo ? ReportDimension.Hour : ReportDimension.Day;
  const identityMetric =
    timeDimension === ReportDimension.Hour ? ReportMetric.Hour : ReportMetric.Day;
  return {
    metrics: [...allIntegrationReportMetrics, identityMetric],
    dimensions: [timeDimension],
    filters: {
      dateRange: dashboardReportDateRange(range),
      dateFrom: range.dateFrom,
      dateTo: range.dateTo,
      demandIntegrationIds: [integrationId],
      exchangeNetworkOnly: false,
    },
    source: ReportType.Exchange,
  };
}

export function previousIntegrationDashboardRange(
  range: DashboardDateRange,
): DashboardDateRange | null {
  if (
    range.preset &&
    [
      DashboardDatePreset.Today,
      DashboardDatePreset.ThisWeek,
      DashboardDatePreset.ThisMonth,
    ].includes(range.preset)
  ) {
    return null;
  }
  const from = parseUtcDate(range.dateFrom);
  const to = parseUtcDate(range.dateTo);
  if (!from || !to) return null;
  const periodDays = Math.floor((to.getTime() - from.getTime()) / dayMilliseconds) + 1;
  const previousTo = new Date(from.getTime() - dayMilliseconds);
  const previousFrom = new Date(previousTo.getTime() - (periodDays - 1) * dayMilliseconds);
  return { dateFrom: isoDate(previousFrom), dateTo: isoDate(previousTo) };
}

export function integrationDashboardMetricValue(
  response: ReportResponse | undefined,
  metric: IntegrationDashboardMetric,
) {
  return integrationMetricValue(response?.total, metric);
}

function integrationMetricValue(row: ReportResponse['total'], metric: IntegrationDashboardMetric) {
  const reportMetric = integrationReportMetric[metric];
  return reportMetric === undefined
    ? integrationEcpm(row)
    : numericReportValue(row?.[reportMetricDefinitions[reportMetric].field]);
}

export function integrationDashboardMetricTimeline(
  response: ReportResponse | undefined,
  metric: IntegrationDashboardMetric,
) {
  return (response?.report ?? [])
    .map((row) => integrationMetricValue(row, metric))
    .filter((value): value is number => value !== undefined);
}

export function integrationPerformanceSeries(
  request: ReportRequest | undefined,
  response: ReportResponse | undefined,
  metrics: readonly IntegrationDashboardMetric[],
): DashboardPerformanceSeries[] {
  if (!request) return [];
  const timeMetric = request.dimensions.includes(ReportDimension.Hour)
    ? ReportMetric.Hour
    : ReportMetric.Day;
  return metrics.map((metric) => ({
    metric,
    label: integrationDashboardMetricLabel(metric),
    unit: integrationMetricUnit(metric),
    separateAxis: metric === IntegrationDashboardMetric.Ecpm,
    valueFormatter: (value) => formatIntegrationMetric(value, metric),
    data: (response?.report ?? []).flatMap((row, index) => {
      const value = integrationMetricValue(row, metric);
      return value === undefined
        ? []
        : [
            {
              x: index,
              label: formatReportMetricValue(
                row[reportMetricDefinitions[timeMetric].field],
                timeMetric,
              ),
              value,
            },
          ];
    }),
  }));
}

function integrationMetricUnit(metric: IntegrationDashboardMetric) {
  if (metric === IntegrationDashboardMetric.Margin) return DashboardChartUnit.Percentage;
  if (
    [
      IntegrationDashboardMetric.Revenue,
      IntegrationDashboardMetric.Profit,
      IntegrationDashboardMetric.Ecpm,
      IntegrationDashboardMetric.Srpm,
    ].includes(metric)
  ) {
    return DashboardChartUnit.Currency;
  }
  return DashboardChartUnit.Count;
}

function dashboardReportDateRange(range: DashboardDateRange) {
  return range.preset ? reportDateRangeByDashboardPreset[range.preset] : null;
}

const reportDateRangeByDashboardPreset = {
  [DashboardDatePreset.Today]: ReportDateRangeType.Today,
  [DashboardDatePreset.Yesterday]: ReportDateRangeType.Yesterday,
  [DashboardDatePreset.ThisWeek]: ReportDateRangeType.ThisWeek,
  [DashboardDatePreset.Last7Days]: ReportDateRangeType.Last7Days,
  [DashboardDatePreset.ThisMonth]: ReportDateRangeType.ThisMonth,
  [DashboardDatePreset.Last30Days]: ReportDateRangeType.Last30Days,
  [DashboardDatePreset.LastMonth]: ReportDateRangeType.LastMonth,
  [DashboardDatePreset.Last3Months]: ReportDateRangeType.Last3Months,
} as const satisfies Record<DashboardDatePreset, ReportDateRangeType>;

const dayMilliseconds = 24 * 60 * 60 * 1000;

function parseUtcDate(value: string) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const [, year, month, day] = match;
  if (!year || !month || !day) return null;
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  return Number.isNaN(date.getTime()) || isoDate(date) !== value ? null : date;
}

function isoDate(value: Date) {
  return value.toISOString().slice(0, 10);
}
